import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import {
  DatePicker,
  DatePickerInput,
  Dropdown,
  InlineNotification,
  TextInput,
} from '@carbon/react';
import type { OverviewResponse } from './types-overview';
import { editOverviewAtom, overviewUrl } from './atoms/overview';
import {
  EDITABLE_OVERVIEW_FIELDS,
  fieldInputValue,
  type OverviewFieldDef,
} from './overview-utils';
import { getDateString, parseUTCdateTime } from 'utils/dates';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

// Edit panel for the DO Overview section. Mirror of the AFE
// `identification-edit-panel.tsx` SidePanel form, but every field submits FLAT
// under `overview` (snake_case — OverviewController#overview_params) with NO
// extension_data block. Driven by the shared OVERVIEW_FIELDS map so the panel
// and the read-only table never diverge. Blank strings normalize to null.
//
// Request bodies stay snake_case; responses are read camelCase (KEEP). Only the
// fields that are BOTH FE-editable (OverviewFieldDef.editable) AND flagged
// editable by the BE (detail.fieldEditable[key]) are rendered — everything else
// stays display-only in the table.

interface Props {
  open: boolean;
  documentId: number | string;
  detail: OverviewResponse;
  onClose: () => void;
  onSaved: () => void;
}

const blank = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

export const OverviewEditPanel: FC<Props> = ({
  open,
  documentId,
  detail,
  onClose,
  onSaved,
}) => {
  const editOverview = useSetAtom(editOverviewAtom);
  // Flat working copy keyed by def.key (string form for every input type).
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable in the panel = FE-editable AND not BE-locked (fieldEditable !== false).
  const panelFields = EDITABLE_OVERVIEW_FIELDS.filter(
    (def) => detail.fieldEditable[def.key] !== false
  );

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    const seeded: Record<string, string> = {};
    panelFields.forEach((def) => {
      seeded[def.key] = fieldInputValue(def, detail.fields);
    });
    setValues(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setPending(true);
    setError(null);

    const overview: Record<string, string | null> = {};
    panelFields.forEach((def) => {
      overview[def.requestKey] = blank(values[def.key] ?? '');
    });

    editOverview({
      url: overviewUrl(documentId),
      method: 'PATCH',
      data: { overview },
      onSuccess: () => {
        setPending(false);
        onSaved();
      },
      onError: (m) => {
        setPending(false);
        setError(m ?? 'Failed to save overview.');
      },
    });
  };

  const renderField = (def: OverviewFieldDef) => {
    const id = `do-overview-${def.key}`;
    const value = values[def.key] ?? '';

    if (def.input === 'dropdown') {
      // Normalize options to {label, value}; preserve an existing value that is
      // not in the option set (e.g. a legacy SF-synced status) so the dropdown
      // never silently clears it. Mirror of the AFE identification edit panel.
      const opts = (def.options ?? []).map((o) => {
        return typeof o === 'string' ? { label: o, value: o } : o;
      });
      if (value && !opts.some((o) => o.value === value)) {
        opts.push({ label: value, value });
      }
      return (
        <Dropdown
          key={def.key}
          id={id}
          light
          titleText={def.label}
          label="Choose an option"
          items={opts}
          itemToString={(item: { label: string; value: string } | null) => {
            return item ? item.label : '';
          }}
          selectedItem={opts.find((o) => o.value === value) ?? null}
          onChange={({
            selectedItem,
          }: {
            selectedItem: { label: string; value: string } | null;
          }) => setValue(def.key, selectedItem?.value ?? '')}
        />
      );
    }

    if (def.input === 'date') {
      return (
        <DatePicker
          key={def.key}
          datePickerType="single"
          // Submit ISO (YYYY-MM-DD via getDateString) so the backend casts the
          // date unambiguously — a US-format MM/DD/YYYY body gets day-first-cast
          // server-side (Jul 3 → Mar 7; Dec 25 → NULL). The working copy is
          // therefore ISO; flatpickr (Carbon's default m/d/Y format) can't parse
          // a bare ISO date, so feed the INPUT a MM/DD/YYYY display string
          // (parseUTCdateTime), while the picked Date rounds back to ISO.
          value={parseUTCdateTime(value)}
          onChange={(dates: Date[]) => {
            if (dates[0]) setValue(def.key, getDateString(dates[0]));
          }}
        >
          <DatePickerInput
            id={id}
            labelText={def.label}
            placeholder="MM/DD/YYYY"
            size="sm"
          />
        </DatePicker>
      );
    }

    return (
      <TextInput
        key={def.key}
        id={id}
        light
        labelText={def.label}
        size="sm"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(def.key, e.target.value)
        }
      />
    );
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Edit Division Order Overview"
      subtitle="Update any field below and click Save to apply your changes."
      slideIn
      selectorPageContent=".do-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save',
          disabled: pending,
          onClick: handleSave,
        },
      ]}
    >
      <div className={`${styles.panelForm} ${styles.panelLightFields}`}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}

        <div className={styles.panelColumn}>{panelFields.map(renderField)}</div>
      </div>
    </SidePanel>
  );
};
